// apps/web/src/components/AIChat.tsx
// AI Chatbox Component using Gemini 2.0 Flash

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { MessageCircle, X, Send, Loader2, RotateCcw, Sparkles, Bot, User, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { trpc } from '@/trpc/client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
};

export interface AIChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AIChat: React.FC<AIChatProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: "👋 Hi! I'm the K&C Logistics AI Assistant. How can I help you today?",
      timestamp: new Date(),
      status: 'sent',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: sendMessage } = trpc.chat.sendMessage.useMutation({
    onSuccess: (response) => {
      if (response.success && response.message) {
        const assistantMessage: ChatMessage = {
          id: Math.random().toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          status: 'sent',
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else if (response.error) {
        toast.error(response.error);
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === prev.length - 1 ? { ...msg, status: 'error' as const } : msg
          )
        );
      }
      setIsLoading(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send message');
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === prev.length - 1 && msg.role === 'user' ? { ...msg, status: 'error' as const } : msg
        )
      );
      setIsLoading(false);
    },
  });

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    if (!isOnline) {
      toast.error('No internet connection. Please check your network.');
      return;
    }

    const userMessage: ChatMessage = {
      id: Math.random().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      status: 'sending',
    };

    setMessages((prev) => [...prev, { ...userMessage, status: 'sent' }]);
    setInput('');
    setIsLoading(true);

    const conversationHistory = messages.slice(1).map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    sendMessage({
      message: input,
      conversationHistory,
    });
  };

  const retryMessage = () => {
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    if (lastUserMessage) {
      setInput(lastUserMessage.content);
      setMessages(prev => prev.filter(m => m.status !== 'error'));
    }
  };

  const resetChat = () => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: "👋 Hi! I'm the K&C Logistics AI Assistant. How can I help you today?",
        timestamp: new Date(),
        status: 'sent',
      },
    ]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-4 z-50 w-full h-full sm:w-full sm:max-w-[400px] sm:h-[600px] sm:max-h-[80vh] flex flex-col"
      >
        <Card className="flex-1 flex flex-col overflow-hidden bg-black/80 backdrop-blur-xl border-0 sm:border border-white/10 shadow-2xl rounded-none sm:rounded-2xl relative">

          {/* Dynamic Background Effect */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden rounded-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 hover:bg-primary/30 rounded-full blur-[100px] transition-colors duration-1000 -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
          </div>

          {/* Header */}
          <div className="relative z-10 p-4 border-b border-white/10 flex items-center justify-between bg-white/5 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-lg shadow-primary/25">
                  <Bot className="text-white w-6 h-6" />
                </div>
                <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-black ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">K&C Assistant</h3>
                <p className="text-[10px] text-white/50 font-mono uppercase tracking-wider">
                  GEMINI 2.0 • {isOnline ? 'ONLINE' : 'OFFLINE'}
                </p>
              </div>
            </div>

            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={resetChat} className="w-8 h-8 rounded-full text-white/70 hover:text-white hover:bg-white/10">
                <RotateCcw size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose} className="w-8 h-8 rounded-full text-white/70 hover:text-white hover:bg-white/10">
                <X size={16} />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 relative z-10 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
            {messages.map((msg, i) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                key={msg.id}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                {msg.role === 'assistant' ? (
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10 flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10 flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}

                <div className={`flex flex-col max-w-[80%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed border ${msg.role === 'user'
                    ? 'bg-primary text-primary-foreground border-primary/20 rounded-tr-sm'
                    : 'bg-white/10 text-white border-white/10 rounded-tl-sm'
                    }`}>
                    {msg.content}
                  </div>
                  <span className="text-[10px] text-white/30 mt-1 px-1">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </motion.div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10 flex-shrink-0 mt-1">
                  <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                </div>
                <div className="bg-white/5 border border-white/10 px-4 py-3 rounded-2xl rounded-tl-sm flex gap-1 items-center">
                  <div className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce delay-75" />
                  <div className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce delay-150" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white/5 border-t border-white/10 relative z-10 backdrop-blur-md">
            <form onSubmit={handleSendMessage} className="relative">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything..."
                disabled={isLoading || !isOnline}
                className="bg-black/20 border-white/10 text-white placeholder:text-white/30 pl-4 pr-12 py-6 rounded-xl focus-visible:ring-primary/50"
              />
              <Button
                type="submit"
                disabled={!input.trim() || isLoading}
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-all"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIChat;
